import * as Sentry from '@sentry/react'
import { supabase } from './supabase.js'

const RETRY_ATTEMPTS = 5
const RETRY_BASE_DELAY_MS = 1000
const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * Wrapper fetch avec injection automatique du token Supabase.
 * Retente automatiquement sur les erreurs réseau transitoires
 * (ECONNRESET / ECONNREFUSED lors du démarrage du backend).
 * Backoff exponentiel : 1s, 2s, 4s, 8s entre les tentatives.
 * Lance une Error si la réponse n'est pas ok.
 */
export async function apiFetch(url, options = {}) {
  const { retry = true, ...fetchOptions } = options
  const { data: { session } } = await supabase.auth.getSession()
  const headers = { 'Content-Type': 'application/json', ...fetchOptions.headers }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }

  const maxAttempts = retry ? RETRY_ATTEMPTS : 1
  let lastError
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1)
      await new Promise(r => setTimeout(r, delay))
    }
    try {
      const res = await fetch(API_BASE + url, { ...fetchOptions, headers })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        const err = new Error(body.error || `Erreur HTTP ${res.status}`)
        // 401 : tentative de refresh de session puis retry une fois
        if (res.status === 401) {
          const { error: refreshError } = await supabase.auth.refreshSession()
          if (!refreshError && retry) {
            return apiFetch(url, { ...options, retry: false })
          }
          await supabase.auth.signOut()
          throw err
        }
        // Erreurs serveur transitoires (5xx) : on retente comme pour les erreurs réseau
        if (res.status >= 500 && attempt < RETRY_ATTEMPTS - 1) {
          lastError = err
          continue
        }
        Sentry.captureException(err, { extra: { url, method: options.method ?? 'GET', status: res.status } })
        throw err
      }
      if (res.status === 204) return null
      return res.json()
    } catch (err) {
      // Erreur réseau (TypeError) : on retente. Erreur HTTP : on remonte immédiatement.
      if (err instanceof TypeError) {
        lastError = err
        continue
      }
      throw err
    }
  }

  throw lastError
}
